import CropSquareIcon from '@mui/icons-material/CropSquare'
import InfoIcon from '@mui/icons-material/Info'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import { styled, useTheme } from '@mui/material/styles'
import Tooltip, { tooltipClasses, TooltipProps } from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { Fragment } from 'react'

import { i18WithParams as t } from '@shared/locale'
import { isEmpty } from 'components-ui/src/shared/common'
import { isVaccinationConf, PreRegValidationData } from './helper'
import { ListLink } from './list_link'

const CustomWidthTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 468,
  },
})

interface TCheckListProps {
  regData: PreRegValidationData | null
  lppId?: string
  useIcons: boolean
  useLinks: boolean
}

export const CheckList = ({ regData, lppId, useIcons = true, useLinks = false }: TCheckListProps) => {
  const theme = useTheme()

  if (!regData) {
    return null
  }

  return (
    <List>
      {regData?.locationUserProfile?.locationPreRegistrationConfs
        ?.filter((reg) => reg.compliant === false)
        ?.map((conf, idx) => (
          <ListItem key={idx} disablePadding sx={{ pb: 1, alignItems: 'center' }}>
            {useIcons && <CropSquareIcon sx={{ color: theme.palette.text.secondary }} />}
            <Typography variant="body1" ml={1} sx={{ whiteSpace: 'nowrap' }}>
              {`${conf.bookingActionName || ''} ${conf.bookingGroupDisplayName || ''}`.trim()}
            </Typography>
            {useLinks ? (
              <ListLink conf={conf} />
            ) : (
              <ListItemText primary={conf.bookingDisplayName} sx={{ pl: 0.5, flex: '0 1 auto' }} />
            )}
          </ListItem>
        ))}
      {regData?.locationPetProfiles
        ?.filter((lpp) => (isEmpty(lppId) ? true : lpp.id === lppId))
        ?.map((pp, index) => {
          return pp.locationPreRegistrationConfs?.map((conf, idx) => {
            if (
              conf.compliant === true &&
              (typeof conf.groups === 'undefined' || conf.groups?.every((g) => g.compliant === true))
            ) {
              return null
            }
            const noCompliantGroups = conf.groups?.filter((g) => g.compliant === false)
            return (
              <ListItem key={`${index}-${idx}`} disablePadding sx={{ pb: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                {useIcons && <CropSquareIcon sx={{ color: theme.palette.text.secondary }} />}
                <Typography variant="body1" ml={1}>
                  {`${conf.bookingActionName || ''} ${pp.displayName || ''}'s ${conf.bookingGroupDisplayName || ''}`}
                </Typography>
                {conf.compliant === true ? null : useLinks ? (
                  <>
                    <ListLink conf={conf} petProfileId={pp.id} />
                    {conf.groups ? ', ' : ''}
                  </>
                ) : (
                  <ListItemText primary={conf.bookingDisplayName} sx={{ pl: 0.5, flex: '0 1 auto' }} />
                )}
                {isVaccinationConf(conf) && (
                  <CustomWidthTooltip title={t('profile.pet.vaccination.notice')} disableFocusListener placement="top">
                    <InfoIcon sx={{ color: theme.palette.text.secondary, ml: 0.5 }} fontSize="small" />
                  </CustomWidthTooltip>
                )}
                {noCompliantGroups?.map((group, idx) => (
                  <Fragment key={idx}>
                    {useLinks ? (
                      <ListLink conf={group} petProfileId={pp.id} />
                    ) : (
                      <ListItemText primary={group.bookingDisplayName} sx={{ pl: 0.5, flex: '0 1 auto' }} />
                    )}
                    {idx < noCompliantGroups.length - 1 ? ', ' : ''}
                  </Fragment>
                ))}
              </ListItem>
            )
          })
        })}
    </List>
  )
}
